export type Listener<T> = (data: T) => void;
export type Unsubscribe = () => void;

export class Stream<T> {
  constructor() {}

  private listeners: Set<Listener<T>> = new Set();

  public on(listener: Listener<T>): Unsubscribe {
    this.listeners.add(listener);

    return () => this.off(listener);
  }

  public off(listener: Listener<T>): void {
    this.listeners.delete(listener);
  }

  public emit(data: T): void {
    this.listeners.forEach((listener) => listener(data));
  }

  public once(listener: Listener<T>): Unsubscribe {
    const wrapper = (data: T) => {
      listener(data);
      this.off(wrapper);
    };
    this.on(wrapper);
    return () => this.off(wrapper);
  }

  public toPromise(): Promise<T> {
    return new Promise((resolve) => this.once(resolve));
  }

  public filter(predicate: (data: T) => boolean): Stream<T> {
    const newStream = new Stream<T>();

    this.on((data) => {
      if (!predicate(data)) {
        return;
      }
      newStream.emit(data);
    });

    return newStream;
  }

  public map<R>(transform: (data: T) => R): Stream<R> {
    const newStream = new Stream<R>();

    this.on((data) => {
      newStream.emit(transform(data));
    });

    return newStream;
  }

  public distinctUntilChanged(): Stream<T> {
    const newStream = new Stream<T>();
    let lastValue: T;

    this.on((value) => {
      if (value !== lastValue) {
        lastValue = value;
        newStream.emit(value);
      }
    });

    return newStream;
  }
}
